import {Mapper} from '@s-ui/domain'

/**
 * @implements Mapper
 */
export default class TrackingAdsListMapper extends Mapper {
  /**
   * @constructor
   * @param {Object} param
   * @param {Object} param.config
   * @param {Object} param.fromPageTrackingJsonToTrackingDataValueObjectMapper
   * @param {Object} param.fromPageTrackingJsonToAdvertisementsValueObjectMapper
   * @param {Object} param.fromSearchJSONToFiltersTrackingMapper
   * @param {Object} param.fromQueryParamsToPageNameValueObjectPageMapper
   */
  constructor({
    config,
    fromPageTrackingJsonToTrackingDataValueObjectMapper,
    fromPageTrackingJsonToAdvertisementsValueObjectMapper,
    fromSearchJSONToFiltersTrackingMapper,
    fromQueryParamsToPageNameValueObjectPageMapper
  }) {
    super()

    this._config = config
    this._appName = this._config.get('APP_NAME')
    this._defaultCategoryId = this._config.get('search').DEFAULT_CATEGORY1_ID
    this._trackingDataByPageName = this._config.get('tracking').TRACKING_DATA_BY_PAGE
    this._categoryId1ToPageNameDictionary = this._config.get('tracking').CATEGORY_ID_1_TO_PAGE_NAME_DICTIONARY
    this._highlightTypes = this._config.get('COMMON').HIGHLIGHT_TYPES
    this._fromPageTrackingJsonToTrackingDataValueObjectMapper = fromPageTrackingJsonToTrackingDataValueObjectMapper
    this._fromPageTrackingJsonToAdvertisementsValueObjectMapper = fromPageTrackingJsonToAdvertisementsValueObjectMapper
    this._fromSearchJSONToFiltersTrackingMapper = fromSearchJSONToFiltersTrackingMapper
    this._fromQueryParamsToPageNameValueObjectPageMapper = fromQueryParamsToPageNameValueObjectPageMapper
  }

  _getLastFilterDescriptor(lastFilter) {
    const key = Object.keys(lastFilter)[0]
    return {
      key,
      value: lastFilter[key]
    }
  }

  _isLastFilterNotEmpty(lastFilter) {
    if (typeof lastFilter === 'undefined') return false

    const {value} = this._getLastFilterDescriptor(lastFilter)

    if (Array.isArray(value) || typeof value === 'string') return value.length > 0
    if (typeof value === 'object') return Object.keys(value).length
    if (typeof value === 'boolean') return true

    return Boolean(value)
  }

  _getHighlightType = ({creationDate, publicationDate, isProfessional, isTop, isUrgent}) => {
    const isSubir = publicationDate !== creationDate
    if (isTop) return this._highlightTypes.top
    if (isSubir) return isProfessional ? this._highlightTypes.subir : this._highlightTypes.renueva
    if (isUrgent) return this._highlightTypes.urge
    return this._highlightTypes.notHighlighted
  }

  _mapWith = fixedProperties => ad => {
    // ad could be an Object or a String, is it is a string we assume that it corresponds to adId
    return typeof ad === 'object'
      ? {...ad, ...fixedProperties, highlightType: this._getHighlightType(ad)}
      : {id: ad, ...fixedProperties}
  }

  _getProductsFrom({adsTypeName, advertisements, listType}) {
    const fixedProperties = {
      quantity: 0
    }

    const advertisementsMapper = this._mapWith(fixedProperties)
    const ads = advertisements.map(advertisementsMapper)

    const {products} = this._fromPageTrackingJsonToAdvertisementsValueObjectMapper
      .setParams({adsTypeName, listType})
      .map({ads})
      .toJSON()

    return products
  }

  _fromPageToPageName = (page = '') => page.replace(/_/g, '-').toLowerCase()

  _fromPageNameToPage = (pageName = '') => pageName.replace(/-/g, '_').toUpperCase()

  // This method returns the page_category_level_x based on the many categories
  // After migrating ads-list in coches, the categories will not be passed as parameter anymore
  // and the categories will be generated internally depending on the {search}
  _getPageCategoryLevels = ({search}) => {
    const {categoryId: category1Id = this._defaultCategoryId} = search
    const pageNameByCategorylevel1Id = this._categoryId1ToPageNameDictionary[category1Id]

    // The configuration is currently in the following format: EXAMPLE_OF_A_NAME
    // so we parse our "ads-list-page-name" to "ADS_LIST_PAGE_NAME"
    const pageNameSnakeCaseUpperCased = this._fromPageNameToPage(pageNameByCategorylevel1Id)
    const trackingConfigByPageName = this._trackingDataByPageName[pageNameSnakeCaseUpperCased] || {}
    const {page_category_level1: pageCategoryLevel1, page_category_level2: pageCategoryLevel2} =
      trackingConfigByPageName
    return {
      page_category_level1: pageCategoryLevel1,
      ...(pageCategoryLevel2 && {page_category_level2: pageCategoryLevel2})
    }
  }

  _getHitOriginByReferer = ({referer}) => {
    if (!referer) return

    const {NEWS_DETAIL_PAGE_NAME} = this._config.get('page')
    const {
      HIT_ORIGINS: {NEWS_DETAIL_REFERER: newsDetailHitOrigin}
    } = this._config.get('tracking')

    const hitOriginsMapping = {
      [NEWS_DETAIL_PAGE_NAME]: newsDetailHitOrigin
    }

    const {pageName} = this._fromQueryParamsToPageNameValueObjectPageMapper
      .map({
        path: referer
      })
      .toJSON()

    return hitOriginsMapping[pageName]
  }

  /**
   * @param {Object} param
   * @param {String} param.page
   * @param {String} param.culture
   * @param {Object} param.data
   * @param {String} param.data.category
   * @param {String} param.data.category2
   * @param {Number} param.data.searchResultsCount
   * @param {Number} param.data.pageNumber
   * @param {Object} param.data.filters
   * @param {Object} param.data.lastFilter
   * @param {Object} param.data.merchantList
   */
  map({page, culture, data}) {
    const emptyResults = this._config.get('EMPTY_RESULTS_NUMBER')
    const {
      advertisements,
      category,
      category2,
      filters: filtersInStringConcated, // key1:value1,key2:value2...
      lastFilter: lastFilterData,
      listingSectionId,
      merchantList,
      origin,
      pageNumber = this._config.get('DEFAULT_PAGE_NUMBER'),
      pageOrigin,
      referer,
      search,
      searchResultsCount
    } = data

    const hitOrigin = origin || (referer && this._getHitOriginByReferer({referer}))

    const {TRACKING_TRANSLATIONS_BY_CULTURE, FILTER_EVENT_NAME} = this._config.get('tracking')
    const {ADS_LIST_FILTERS_PAGE_NAME, ADS_LIST_SUBSCRIPTION_PAGE_NAME} = this._config.get('page')
    const TRANSLATIONS_BY_CULTURE = TRACKING_TRANSLATIONS_BY_CULTURE[culture]

    const {
      filters: searchFilters, // This has the right format already, key1:value1,key2:value2...
      lastFilter
    } = this._fromSearchJSONToFiltersTrackingMapper.map({
      search,
      lastFilter: lastFilterData,
      listingSectionId
    })
    const filters = filtersInStringConcated || searchFilters

    const pageName =
      searchResultsCount === emptyResults
        ? TRANSLATIONS_BY_CULTURE.NO_RESULTS_PAGE_NAME
        : TRANSLATIONS_BY_CULTURE[`${page}_PAGE_NAME`]

    const pageNameDomainFormat = this._fromPageToPageName(page)
    const listType = TRANSLATIONS_BY_CULTURE[`${page}_TYPE`]
    // MerchantList is the legacy name for this. To use the new one called 'advertisements',
    // we must update how the monolith calls the tracking plant and change merchantList for advertisements
    const legacyProducts =
      Boolean(merchantList) &&
      this._getProductsFrom({
        adsTypeName: pageNameDomainFormat === ADS_LIST_SUBSCRIPTION_PAGE_NAME && 'ad_impression',
        advertisements: merchantList,
        listType
      })
    const newProducts =
      Boolean(advertisements) &&
      this._getProductsFrom({
        adsTypeName: pageNameDomainFormat === ADS_LIST_SUBSCRIPTION_PAGE_NAME && 'ad_impression',
        advertisements,
        listType
      })
    const products = newProducts || legacyProducts
    const hasFilters = Boolean(filters && Object.keys(filters).length > 0)
    const hasProducts = Boolean(Object.keys(products).length)

    // we convert the page received to kebab-case because it's what we use in our domain
    const isFiltersView = pageNameDomainFormat === ADS_LIST_FILTERS_PAGE_NAME
    if (isFiltersView) {
      // In order to properly tag the page where filters are applied on mobile,
      // we need to know the origin page ie the page that triggered the filters modal
      const pageOriginName = TRANSLATIONS_BY_CULTURE[`${pageOrigin}_PAGE_NAME`]
      // In the tagging plan definition, when it's a filters view, we have to pass ALWAYS the page_category_level1.
      // In the monolith, we were passing the category generated by backend to the use case execution.
      // From now on, we generate it on frontend given the search.category1Id
      //
      // We check if there's a "category" to make it retro compatible with the current usage,
      // but in the future they will not be passed anymore
      // and the page_category_level_1 will be generated based on the search object
      return this._fromPageTrackingJsonToTrackingDataValueObjectMapper.setParams({page, culture}).map({
        page_name: pageName + `${pageOriginName ? ' ' + pageOriginName : ''}`,
        // @TODO: After migrating the ads list page in coches, the variable "category" will not be used anymore.
        // Make sure to remove its usage
        ...(category && {page_category_level1: category}),
        ...(!category && this._getPageCategoryLevels({search}))
      })
    }

    const hasProvinces = hasFilters && Boolean(filters.provinces?.length)
    const defaultProvinces = hasProvinces ? filters.provinces : 'all'

    // default filters do not apply to subscription ads list page
    const defaultFilters =
      pageNameDomainFormat === ADS_LIST_SUBSCRIPTION_PAGE_NAME
        ? {}
        : {
            certification: false,
            financedPrice: 'contado',
            warranty: false,
            provinces: defaultProvinces
          }

    return this._fromPageTrackingJsonToTrackingDataValueObjectMapper.setParams({page, culture}).map({
      page_name: pageName,
      search_result_number: searchResultsCount,
      list_page_num: pageNumber,
      ...(this._isLastFilterNotEmpty(lastFilter) && {
        event_name: FILTER_EVENT_NAME,
        last_filter: lastFilter
      }),
      ...(category && {page_category_level1: category}),
      ...(category2 && {page_category_level2: category2}),
      ...(hasProducts && {products}),
      ...(hasFilters && {
        filters: {...defaultFilters, ...filters},
        ...(hitOrigin && {hit_origin: hitOrigin})
      })
    })
  }
}
